import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Link,
  ButtonBase,
  Button,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  Cloud as CloudIcon,
  Code as CodeIcon,
  Description as DescriptionIcon,
  WorkHistory as WorkHistoryIcon,
  CalendarMonth as CalendarIcon,
  Psychology as PsychologyIcon,
  BugReport as BugReportIcon,
  Hub as HubIcon,
} from '@mui/icons-material';
import { useChat } from './ChatContext';
import { ChatMessage } from '../../types/chat';
import { getChatLeadFormCopy } from '../../utils/chatLeadFormI18n';
import dataDrivenLogo from '../../assets/images/logo/Logo_DataDriven.png';
import alioLogo from '../../assets/images/alioforlogo.png';
import UndoIcon from '../../assets/images/majesticons/undo.svg';
import RocketIcon from '../../assets/images/majesticons/rocket-3-start.svg';
import ChatIcon from '../../assets/images/majesticons/chat-2.svg';

// Topic options for the welcome screen
const TOPIC_OPTIONS = [
  {
    label: 'Cloud & DevOps',
    message: 'Tell me about your Cloud Migration and DevOps services.',
    icon: CloudIcon,
    color: '#4FC3F7',
  },
  {
    label: 'Salesforce',
    message: 'What Salesforce CRM implementation and consulting services do you offer?',
    icon: HubIcon,
    color: '#00A1E0',
  },
  {
    label: 'Custom Software',
    message: 'Tell me about your custom software development capabilities.',
    icon: CodeIcon,
    color: '#66BB6A',
  },
  {
    label: 'AI & Machine Learning',
    message: 'What AI and machine learning services does Alio provide?',
    icon: PsychologyIcon,
    color: '#AB47BC',
  },
  {
    label: 'QA & Testing',
    message: 'Tell me about your QA automation and testing services.',
    icon: BugReportIcon,
    color: '#FFA726',
  },
  {
    label: 'White Papers',
    message: 'What white papers and resources do you have available?',
    icon: DescriptionIcon,
    color: '#78909C',
  },
  {
    label: 'Case Studies',
    message: 'Can you share some of your case studies and client success stories?',
    icon: WorkHistoryIcon,
    color: '#EF5350',
  },
  {
    label: 'Schedule a Consultation',
    message: 'I\'d like to schedule a consultation to discuss how Alio can help my business.',
    icon: CalendarIcon,
    color: '#FF6B35',
  },
];

/**
 * Models often emit broken markdown: a full URL as "label" plus `](` + same URL, but omit the
 * leading `[`, producing `https://x](https://x`. Naive URL regex then captures one invalid href.
 */
function preprocessAiChatLinks(text: string): string {
  let s = text;
  // Standard markdown [label](url) → show destination only
  s = s.replace(/\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)/g, '$2');
  // Broken: url](url) with closing )
  s = s.replace(/(https?:\/\/[^\s\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '$2');
  // Broken: url](url with no closing )
  s = s.replace(/(https?:\/\/[^\s\]]+)\]\((https?:\/\/[^\s\]]+)/g, '$2');
  return s;
}

/**
 * LLMs often emit markdown like [text](url) or paste urls followed by ] ) ' etc.
 * The loose url regex captures those; strip so href is valid.
 */
function sanitizeChatUrl(raw: string): string {
  return raw
    .trim()
    .replace(/^\[+/, '')
    .replace(/^\(+/, '')
    .replace(/^[<'"]+/, '')
    .replace(/[.,;:!?)\]\>'"`*~_]+$/, '');
}

// Helper to produce a friendly display label for known URLs
const getUrlDisplayLabel = (url: string): string => {
  const cleaned = sanitizeChatUrl(url);

  // Calendly
  if (cleaned.includes('calendly.com')) return 'Schedule a Consultation';

  // Alio site links
  if (cleaned.includes('alioit.com')) {
    // Case studies
    const caseStudyMatch = cleaned.match(/\/case-studies\/([a-z0-9-]+)/);
    if (caseStudyMatch) {
      const slug = caseStudyMatch[1];
      const labels: Record<string, string> = {
        'time4learning': 'Case Study: Time4Learning',
        'nerito': 'Case Study: Nerito',
        'industry-4-0-for-all': 'Case Study: Industry 4.0',
        'salesforce': 'Case Study: Higher Ed Salesforce',
        'fintech-app': 'Case Study: Fintech App',
        'e-learning-platform': 'Case Study: E-Learning Platform',
        'operoute': 'Case Study: Operoute PLM',
        'ninjaone': 'Case Study: NinjaOne',
        'ai-qa-automation': 'Case Study: AI QA Automation',
      };
      return labels[slug] || `Case Study: ${slug}`;
    }

    if (cleaned.includes('/careers-opportunity')) {
      return 'Submit Your Resume & Quick Challenge';
    }

    // White papers page
    if (cleaned.includes('/white-papers')) return 'View White Papers';

    // Other known pages
    if (cleaned.includes('/services')) return 'View Services';
    if (cleaned.includes('/company')) return 'About Alio';
    if (cleaned.includes('/expertise')) return 'View Expertise';
    if (cleaned.includes('/blog')) return 'Visit Blog';
    if (cleaned.includes('/community')) return 'Community';

    // Specialized sites
    if (cleaned.includes('vciso.alioit.com')) return 'Virtual CISO Service';
    if (cleaned.includes('healthtech.alioit.com')) return 'HealthTech Security';
  }

  // Spotify podcast
  if (cleaned.includes('spotify.com')) return 'Listen on Spotify';

  // Twitter
  if (cleaned.includes('twitter.com') || cleaned.includes('x.com')) return 'Follow on Twitter';

  // Fallback: show the URL itself
  return cleaned;
};

// Message Content Component for rendering links
const MessageContent: React.FC<{ content: string }> = ({ content }) => {
  const normalized = preprocessAiChatLinks(content);
  // Do not treat `]` as part of a URL — stops `...meet](https://...` from one bad href
  const urlRegex = /(https?:\/\/[^\s\]]+)/g;
  const parts = normalized.split(urlRegex);

  return (
    <Typography 
      variant="body2" 
      sx={{ 
        whiteSpace: 'pre-wrap',
        color: 'white',
        fontSize: '0.875rem',
        lineHeight: 1.4,
        fontWeight: 400,
        textShadow: 'none',
      }}
    >
      {parts.map((part, index) => {
        if (/^https?:\/\/[^\s\]]+$/.test(part)) {
          const cleanUrl = sanitizeChatUrl(part);
          const urlStart = part.lastIndexOf(cleanUrl);
          const trailingChars =
            urlStart >= 0 ? part.slice(urlStart + cleanUrl.length) : '';
          const isCalendly = cleanUrl.includes('calendly.com');
          const isAlioSite = cleanUrl.includes('alioit.com');
          const displayLabel = getUrlDisplayLabel(cleanUrl);
          const showAsChip = isCalendly || isAlioSite;

          return (
            <React.Fragment key={index}>
              {showAsChip ? (
                <Link
                  href={cleanUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'white',
                    backgroundColor: isCalendly ? '#FF6B35' : 'rgba(79, 195, 247, 0.15)',
                    border: isCalendly ? 'none' : '1px solid rgba(79, 195, 247, 0.4)',
                    borderRadius: '12px',
                    padding: '2px 10px',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    textDecoration: 'none',
                    lineHeight: 1.6,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: isCalendly ? '#E55A2B' : 'rgba(79, 195, 247, 0.3)',
                      textDecoration: 'none',
                    },
                  }}
                >
                  {isCalendly ? '📅 ' : '🔗 '}{displayLabel}
                </Link>
              ) : (
                <Link
                  href={cleanUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: '#4FC3F7',
                    textDecoration: 'underline',
                    fontWeight: 400,
                    '&:hover': {
                      color: '#29B6F6',
                    },
                  }}
                >
                  {displayLabel}
                </Link>
              )}
              {trailingChars}
            </React.Fragment>
          );
        }
        return part;
      })}
    </Typography>
  );
};

interface ChatWidgetProps {
  autoOpenOnDesktop?: boolean;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ autoOpenOnDesktop = true }) => {
  const [mounted, setMounted] = useState(false);
  const { state, sendMessage, toggleChat, clearChat, submitLeadForm, dismissLeadForm, openHumanChat } =
    useChat();
  const leadCopy = getChatLeadFormCopy(state.leadForm?.language);
  const [inputValue, setInputValue] = useState('');
  const [leadFirstName, setLeadFirstName] = useState('');
  const [leadLastName, setLeadLastName] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadCompany, setLeadCompany] = useState('');
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatSlot = useRef<'owner' | 'extra' | null>(null);
  if (chatSlot.current === null) {
    const flag = globalThis as { __alioChat?: boolean };
    if (!flag.__alioChat) {
      flag.__alioChat = true;
      chatSlot.current = 'owner';
    } else {
      chatSlot.current = 'extra';
    }
  }
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  /** Ensures desktop "open once on load" does not fire again when the user closes the panel */
  const hasAutoOpenedDesktopRef = useRef(false);
  const [scrollState, setScrollState] = useState({
    canScrollUp: false,
    canScrollDown: true,
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const updateScrollState = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const threshold = 8;
    const canScrollUp = container.scrollTop > threshold;
    const canScrollDown =
      container.scrollTop + container.clientHeight < container.scrollHeight - threshold;

    setScrollState((current) => {
      if (
        current.canScrollUp === canScrollUp &&
        current.canScrollDown === canScrollDown
      ) {
        return current;
      }

      return { canScrollUp, canScrollDown };
    });
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!autoOpenOnDesktop || !mounted || isMobile || hasAutoOpenedDesktopRef.current) return;
    hasAutoOpenedDesktopRef.current = true;
    // Initial chat state is closed; open once on desktop after mount (do not re-run when user closes)
    toggleChat();
  }, [autoOpenOnDesktop, mounted, isMobile, toggleChat]);

  useEffect(() => {
    scrollToBottom();
  }, [state.messages]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(updateScrollState);
    const shortDelay = window.setTimeout(updateScrollState, 100);
    const longDelay = window.setTimeout(updateScrollState, 500);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(shortDelay);
      window.clearTimeout(longDelay);
    };
  }, [state.isOpen, state.messages, state.isLoading, state.challenge, isMobile]);

  useEffect(() => {
    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, []);

  // Handle mobile viewport height changes when keyboard appears/disappears
  useEffect(() => {
    if (!isMobile) return;

    const handleViewportChange = () => {
      // Force a re-render when viewport height changes
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Set initial viewport height
    handleViewportChange();

    // Listen for viewport changes
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
    };
  }, [isMobile]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+K or Cmd+K to toggle chat
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        toggleChat();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleChat]);

  const handleSendMessage = async () => {
    if (inputValue.trim()) {
      const message = inputValue.trim();
      setInputValue('');
      await sendMessage(message);
    }
  };

  const handleTopicClick = async (topicMessage: string) => {
    await sendMessage(topicMessage);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleScrollControlClick = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const threshold = 8;
    const canScrollDown =
      container.scrollTop + container.clientHeight < container.scrollHeight - threshold;
    const targetTop = canScrollDown
      ? Math.min(container.scrollTop + container.clientHeight * 0.8, container.scrollHeight)
      : 0;

    container.scrollTo({
      top: targetTop,
      behavior: 'smooth',
    });

    window.setTimeout(updateScrollState, 300);
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const MessageBubble: React.FC<{ message: ChatMessage }> = ({ message }) => {
    const isUser = message.role === 'user';

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          mb: 2,
          gap: 1,
        }}
      >
        {!isUser && (
          <Box
            component="img"
            src={RocketIcon}
            alt="Alio AI Agent"
            sx={{
              width: 24,
              height: 24,
              filter: 'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(330deg) brightness(118%) contrast(119%)',
              opacity: 0.9,
            }}
          />
        )}
        <Box
          sx={{
            maxWidth: '85%',
            backgroundColor: isUser ? '#FF6B35' : 'rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            p: 1.5,
            position: 'relative',
          }}
        >
          <MessageContent content={message.content} />
          {message.isStreaming && (
            <Box
              component="span"
              aria-hidden
              sx={{
                display: 'inline-block',
                width: '0.5ch',
                height: '1em',
                ml: 0.25,
                verticalAlign: 'text-bottom',
                backgroundColor: 'rgba(255,255,255,0.85)',
                animation: 'chatCaretBlink 1s step-end infinite',
                '@keyframes chatCaretBlink': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0 },
                },
              }}
            />
          )}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.5,
              opacity: 0.7,
              fontSize: '0.7rem',
              color: 'white', // Ensure timestamp is white for visibility
            }}
          >
            {formatTime(message.timestamp)}
          </Typography>
        </Box>
        {isUser && (
          <Avatar
            sx={{
              width: 24,
              height: 24,
              bgcolor: '#FF6B35',
              fontSize: '0.75rem',
            }}
          >
            <PersonIcon sx={{ fontSize: '0.875rem' }} />
          </Avatar>
        )}
      </Box>
    );
  };

  useEffect(() => {
    if (chatSlot.current !== 'owner') return;
    (globalThis as { __alioChat?: boolean }).__alioChat = true;
    return () => {
      (globalThis as { __alioChat?: boolean }).__alioChat = false;
    };
  }, []);

  if (!mounted || chatSlot.current === 'extra') {
    return null;
  }

  if (!state.isOpen) {
    return (
      <ButtonBase
        onClick={toggleChat}
        sx={{
          position: 'fixed',
          right: { xs: 16, sm: 24 },
          bottom: { xs: 96, sm: 104 },
          zIndex: 1200,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.25,
          borderRadius: '8px',
          backgroundColor: '#121212',
          border: '1px solid rgba(255, 107, 53, 0.55)',
          color: 'white',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
          '&:hover': {
            backgroundColor: '#1F1F1F',
            borderColor: '#FF6B35',
          },
        }}
        aria-label="Open Alio AI Agent"
      >
        <Box
          component="img"
          src={ChatIcon}
          alt=""
          sx={{
            width: 18,
            height: 18,
            filter: 'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(330deg) brightness(118%) contrast(119%)',
          }}
        />
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
          Alio AI Agent
        </Typography>
      </ButtonBase>
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        right: isMobile ? 16 : 24,
        bottom: isMobile ? 96 : 104,
        width: isMobile ? 'calc(100% - 32px)' : 380,
        height: isMobile ? 'min(560px, calc((var(--vh, 1vh) * 100) - 176px))' : 560,
        maxHeight: isMobile ? 'calc((var(--vh, 1vh) * 100) - 176px)' : 'calc(100vh - 128px)',
        backgroundColor: '#121212',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: '8px',
        overflow: 'hidden',
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 16px 48px rgba(0, 0, 0, 0.42)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: theme.palette.primary.main,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="img"
            src={dataDrivenLogo}
            alt="Alio AI Agent"
            sx={{
              height: 24,
              width: 'auto',
              objectFit: 'contain',
            }}
          />
          <Typography variant="h6" sx={{ ml: 1, fontSize: '1rem' }}>
            Alio AI Agent
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Button
            size="small"
            onClick={openHumanChat}
            sx={{
              color: 'white',
              textTransform: 'none',
              fontSize: '0.7rem',
              fontWeight: 600,
              px: 1,
              minWidth: 0,
              border: '1px solid rgba(255,255,255,0.35)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.12)',
                borderColor: 'rgba(255,255,255,0.55)',
              },
            }}
            title="Talk to a human on HubSpot chat"
          >
            Human
          </Button>
          <IconButton
            size="small"
            onClick={clearChat}
            sx={{ color: 'white' }}
            title="Clear chat"
          >
            <Box
              component="img"
              src={UndoIcon}
              alt="Clear chat"
              sx={{
                width: 16,
                height: 16,
                filter: 'brightness(0) invert(1)',
              }}
            />
          </IconButton>
          <IconButton
            size="small"
            onClick={toggleChat}
            sx={{ color: 'white' }}
            title="Close chat"
          >
            <CloseIcon sx={{ fontSize: '1.25rem' }} />
          </IconButton>
        </Box>
      </Box>

      {/* Messages Body */}
      <Box
        ref={messagesContainerRef}
        onScroll={updateScrollState}
        sx={{
          flexGrow: 1,
          overflowY: 'scroll',
          p: 2,
          pb: 5,
          pr: 3,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          scrollbarWidth: 'thin',
          scrollbarColor: '#FF6B35 rgba(255, 255, 255, 0.18)',
          '&::-webkit-scrollbar': {
            width: 12,
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.14)',
            borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#FF6B35',
            borderRadius: '8px',
            border: '3px solid #121212',
            minHeight: 48,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#E55A2B',
          },
          // Mobile-specific improvements
          ...(isMobile && {
            paddingBottom: '1rem', // Extra padding to ensure content isn't hidden behind input
            pb: 5,
            minHeight: 0, // Allow flex shrinking
            // Ensure smooth scrolling on mobile
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': {
              width: 10,
            },
            '&::-webkit-scrollbar-thumb': {
              borderWidth: 2,
            },
          }),
        }}
      >
        {state.messages.length === 0 && !state.isLoading ? (
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              color: theme.palette.text.secondary,
            }}
          >
            {/* Logo and greeting */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2, pb: 1 }}>
              <Box
                component="img"
                src={alioLogo}
                alt="Alio IT Solutions"
                sx={{
                  height: 36,
                  width: 'auto',
                  mb: 1.5,
                  opacity: 0.9,
                  objectFit: 'contain',
                }}
              />
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', color: 'white', mb: 0.5 }}>
                Welcome to Alio!
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.7)', mb: 2, px: 1 }}>
                What would you like to discuss today?
              </Typography>
            </Box>

            {/* Topic options grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1,
                px: 1,
                pb: 2,
              }}
            >
              {TOPIC_OPTIONS.map((topic) => {
                const TopicIcon = topic.icon;
                return (
                  <ButtonBase
                    key={topic.label}
                    onClick={() => handleTopicClick(topic.message)}
                    disabled={state.isLoading}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.75,
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      backgroundColor: 'rgba(255, 255, 255, 0.04)',
                      transition: 'all 0.2s ease',
                      minHeight: 72,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderColor: topic.color,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 2px 8px ${topic.color}33`,
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                      '&:disabled': {
                        opacity: 0.5,
                      },
                    }}
                  >
                    <TopicIcon
                      sx={{
                        fontSize: '1.25rem',
                        color: topic.color,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        lineHeight: 1.3,
                        textAlign: 'center',
                      }}
                    >
                      {topic.label}
                    </Typography>
                  </ButtonBase>
                );
              })}
            </Box>

            {/* Keyboard shortcut tip */}
            <Typography variant="caption" sx={{ opacity: 0.6, fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.5)', pb: 1, px: 1 }}>
              Tip: Press Ctrl+K (or Cmd+K) to toggle Alio AI Agent anytime
            </Typography>
          </Box>
        ) : (
          state.messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))
        )}

        {/* Loading indicator — hide once streaming tokens start appearing */}
        {state.isLoading &&
          !state.messages.some((m) => m.role === 'assistant' && m.isStreaming && m.content) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              mb: 2,
              gap: 1,
            }}
          >
            <Box
              component="img"
              src={RocketIcon}
              alt="Alio AI Agent"
              sx={{
                width: 24,
                height: 24,
                filter: 'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(330deg) brightness(118%) contrast(119%)',
                opacity: 0.9,
              }}
            />
            <Box
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CircularProgress size={14} />
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem', color: 'white' }}>
                Thinking...
              </Typography>
            </Box>
          </Box>
        )}

        {state.challenge && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              mb: 2,
              gap: 1,
            }}
          >
            <Box
              component="img"
              src={RocketIcon}
              alt="Alio AI Agent"
              sx={{
                width: 24,
                height: 24,
                filter: 'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(330deg) brightness(118%) contrast(119%)',
                opacity: 0.9,
              }}
            />
            <Box
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                p: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                maxWidth: '85%',
              }}
            >
              <Typography variant="body2" sx={{ color: 'white', fontSize: '0.875rem' }}>
                Answer the verification question using the message box below.
              </Typography>
            </Box>
          </Box>
        )}

        <div ref={messagesEndRef} />
        {(scrollState.canScrollUp || scrollState.canScrollDown) && (
          <ButtonBase
            onClick={handleScrollControlClick}
            aria-label={
              scrollState.canScrollDown
                ? 'Scroll down in Alio AI Agent'
                : 'Scroll to top in Alio AI Agent'
            }
            sx={{
              position: 'absolute',
              right: 24,
              bottom: 10,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.75,
              minHeight: 36,
              borderRadius: '8px',
              backgroundColor: 'rgba(18, 18, 18, 0.95)',
              border: '1px solid rgba(255, 107, 53, 0.8)',
              color: '#FFB18C',
              boxShadow: '0 6px 18px rgba(0, 0, 0, 0.35)',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(255, 107, 53, 0.14)',
              },
              '&:focus-visible': {
                outline: '2px solid #FFB18C',
                outlineOffset: 2,
              },
            }}
          >
            <Typography
              aria-hidden="true"
              sx={{
                fontSize: '1rem',
                lineHeight: 1,
              }}
            >
              {scrollState.canScrollDown ? '↓' : '↑'}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>
              {scrollState.canScrollDown ? 'More' : 'Top'}
            </Typography>
          </ButtonBase>
        )}
      </Box>

      {/* Error Alert */}
      {state.error && (
        <Alert severity="error" sx={{ m: 2, fontSize: '0.875rem' }}>
          {state.error}
        </Alert>
      )}

      {/* HubSpot Redirect Alert */}
      {state.hubSpotRedirect && (
        <Alert 
          severity="info" 
          sx={{ 
            m: 2, 
            fontSize: '0.875rem',
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            border: '1px solid rgba(25, 118, 210, 0.3)',
            '& .MuiAlert-icon': {
              color: '#1976d2',
            },
            '& .MuiAlert-message': {
              color: 'white',
            }
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500, mb: 0.5 }}>
              🤖 AI Assistant Limit Reached
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.8rem' }}>
              {state.hubSpotRedirect.message}
            </Typography>
            {state.hubSpotRedirect.retryAfter && (
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem', display: 'block', mt: 0.5 }}>
                ⏱️ You can try again in {Math.ceil(state.hubSpotRedirect.retryAfter / 60)} minutes
              </Typography>
            )}
          </Box>
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Button
              size="small"
              variant="contained"
              onClick={openHumanChat}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#FF6B35',
                '&:hover': { backgroundColor: '#e55a28' },
              }}
            >
              Talk to a human
            </Button>
          </Box>
        </Alert>
      )}

      {/* In-chat HubSpot lead capture */}
      {state.leadForm?.visible && (
        <Box
          sx={{
            mx: 2,
            mb: 1,
            p: 1.5,
            borderRadius: 1,
            border: '1px solid rgba(109, 252, 255, 0.35)',
            backgroundColor: 'rgba(109, 252, 255, 0.06)',
          }}
        >
          <Typography variant="body2" sx={{ color: '#6DFCFF', fontWeight: 600, mb: 0.5 }}>
            {leadCopy.title}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', display: 'block', mb: 1 }}>
            {state.leadForm.reason || leadCopy.defaultReason}
          </Typography>
          {state.leadForm.requestSummary && (
            <Box
              sx={{
                mb: 1.25,
                p: 1,
                borderRadius: 1,
                backgroundColor: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', mb: 0.5 }}
              >
                {leadCopy.requestLabel}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: '0.8rem',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {state.leadForm.requestSummary}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder={leadCopy.firstName}
                value={leadFirstName}
                onChange={(e) => setLeadFirstName(e.target.value)}
                sx={{
                  flex: 1,
                  minWidth: 120,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  },
                }}
              />
              <TextField
                size="small"
                placeholder={leadCopy.lastName}
                value={leadLastName}
                onChange={(e) => setLeadLastName(e.target.value)}
                sx={{
                  flex: 1,
                  minWidth: 120,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  },
                }}
              />
            </Box>
            <TextField
              size="small"
              placeholder={leadCopy.email}
              type="email"
              value={leadEmail}
              onChange={(e) => setLeadEmail(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                },
              }}
            />
            <TextField
              size="small"
              placeholder={leadCopy.company}
              value={leadCompany}
              onChange={(e) => setLeadCompany(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                },
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => dismissLeadForm()}
                sx={{ color: 'rgba(255,255,255,0.7)' }}
              >
                {leadCopy.notNow}
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={leadSubmitting || !leadEmail.trim()}
                onClick={async () => {
                  setLeadSubmitting(true);
                  try {
                    await submitLeadForm({
                      firstName: leadFirstName,
                      lastName: leadLastName,
                      email: leadEmail,
                      company: leadCompany,
                    });
                    setLeadFirstName('');
                    setLeadLastName('');
                    setLeadEmail('');
                    setLeadCompany('');
                  } catch {
                    /* error surfaced via chat context */
                  } finally {
                    setLeadSubmitting(false);
                  }
                }}
              >
                {leadSubmitting ? leadCopy.sending : leadCopy.send}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Input */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: '#121212',
          // Ensure input area is always visible on mobile and doesn't overlap with HubSpot
          ...(isMobile && {
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            paddingBottom: 'calc(2px + env(safe-area-inset-bottom, 0px))',
            marginRight: 0,
            maxWidth: '100%',
          }),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'flex-end',
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={isMobile ? 2 : 3} // Reduce max rows on mobile
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={state.challenge ? 'Answer the verification question...' : "Ask me anything about Alio's services..."}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                fontSize: isMobile ? '1rem' : '0.875rem', // Larger font on mobile for better touch targets
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiInputBase-input': {
                color: 'white',
                fontSize: isMobile ? '1rem' : '0.875rem',
                '&::placeholder': {
                  color: 'rgba(255, 255, 255, 0.5)',
                  opacity: 1,
                },
              },
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || state.isLoading}
            sx={{
              backgroundColor: '#FF6B35',
              color: 'white',
              width: isMobile ? 44 : 36, // Larger touch target on mobile
              height: isMobile ? 44 : 36,
              '&:hover': {
                backgroundColor: '#E55A2B',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            <SendIcon sx={{ fontSize: isMobile ? '1.25rem' : '1rem' }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatWidget; 
